import React, { ReactNode, useCallback, useMemo } from 'react';

import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import {
  CustomDropdownEntry,
  TreeDropdownExtendedProps,
  TreeDropdownUnionProps
} from 'types/props/TreeDropdownExtendedProps';

import { CustomDropdown } from 'ui/custom-controls';
import SearchInputWithClear from 'ui/custom-controls/SearchInputWithClear';

import { selectedDate } from '../../types/interfaces';
import Datepicker from '../custom-controls/Datepicker';
import TreeDropdownCheckbox from '../custom-controls/TreeDropdownCheckbox';

interface TablePageHeaderProps {
  globalSearch?: boolean;
  isMobile?: boolean;
  datepicker?: boolean;
  onGlobalSearchChange?: (newValue: string) => void;
  onDatePickerChange?: (selection: selectedDate) => void;
  onCalendarChange?: (selection: Date) => void;
  singleCalendarMode?: boolean;
  datepickerDate?: selectedDate | Date | null;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  treeDropdowns?: TreeDropdownUnionProps[];
  customButtonsLeft?: ReactNode[];
  customButtonsRight?: ReactNode[];
  customAddButton?: ReactNode;
  children?: ReactNode;
  additionalLine?: ReactNode;
  tableName: string;
  dropdownWidth?: string;
  datepickerTooltip?: {
    from?: string;
    until?: string;
  };
  datepickerLoading?: boolean;
  hasHorizontalScroll?: boolean;
}

const TablePageHeader: React.FC<TablePageHeaderProps> = ({
  children,
  isMobile,
  customButtonsLeft,
  customButtonsRight,
  customAddButton,
  onAdd,
  onEdit,
  onDelete,
  treeDropdowns,
  onGlobalSearchChange,
  onDatePickerChange,
  singleCalendarMode,
  onCalendarChange,
  datepickerDate,
  additionalLine,
  globalSearch = true,
  datepicker,
  tableName,
  dropdownWidth,
  datepickerTooltip,
  datepickerLoading,
  hasHorizontalScroll = false
}) => {
  const onGlobal = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      onGlobalSearchChange ? onGlobalSearchChange(e.target.value) : {},
    [onGlobalSearchChange]
  );

  const treeDropdownsComponents = useMemo(() => {
    if (!treeDropdowns) return null;

    return treeDropdowns.map((x) => {
      if ('mode' in x && x.mode === 'custom') {
        const custom = x as CustomDropdownEntry;
        return (
          <React.Fragment key={custom.key}>
            <div style={{ width: dropdownWidth ? dropdownWidth : '270px' }}>
              <CustomDropdown {...custom.dropdownProps} />
            </div>
          </React.Fragment>
        );
      }

      const dropdown = x as TreeDropdownExtendedProps;
      return (
        <React.Fragment key={dropdown.key}>
          {dropdown.isLoading ? (
            <p>{dropdown.loadingText ?? ''}</p>
          ) : (
            // TODO заменить на CustomDropdown
            <TreeDropdownCheckbox
              className={!dropdown.isRed ? '' : 'filter-none'}
              {...dropdown}
            />
          )}
        </React.Fragment>
      );
    });
  }, [dropdownWidth, treeDropdowns]);

  const handleGlobalSearchChange = useCallback(
    (newValue: string) => {
      onGlobalSearchChange?.(newValue);
    },
    [onGlobalSearchChange]
  );

  return (
    <div className="flex flex-column">
      <div id="table-header" className="flex justify-content-between gap-2">
        <div className={'flex gap-2'} style={isMobile ? { width: '100%' } : {}}>
          {globalSearch && (
            <SearchInputWithClear
              onChange={handleGlobalSearchChange}
              placeholder="Поиск по ключевым словам"
              isMobile={isMobile}
            />
          )}
          {treeDropdownsComponents}
          {children}
          {datepicker &&
            (singleCalendarMode ? (
              datepickerLoading ? (
                <p>Загрузка календаря...</p>
              ) : (
                <div className="single-calendar-wrapper">
                  <Button
                    icon="pi pi-chevron-left"
                    className="p-button-text"
                    onClick={() => {
                      const cur = (datepickerDate as Date) || new Date();
                      const prev = new Date(
                        cur.getFullYear(),
                        cur.getMonth() - 1,
                        cur.getDate()
                      );
                      onCalendarChange?.(prev);
                    }}
                  />
                  <Calendar
                    value={datepickerDate as Date}
                    onChange={(e) => {
                      const dateValue = e.value as Date;
                      onCalendarChange?.(dateValue);
                    }}
                    view="month"
                    dateFormat="mm.yy"
                    showIcon
                  />
                  <Button
                    icon="pi pi-chevron-right"
                    className="p-button-text"
                    onClick={() => {
                      const cur = (datepickerDate as Date) || new Date();
                      const next = new Date(
                        cur.getFullYear(),
                        cur.getMonth() + 1,
                        cur.getDate()
                      );
                      onCalendarChange?.(next);
                    }}
                  />
                </div>
              )
            ) : datepickerLoading ? (
              <p>Загрузка диапазона...</p>
            ) : (
              <Datepicker
                datepickerDate={datepickerDate as selectedDate}
                onChange={onDatePickerChange}
                customTooltips={datepickerTooltip}
                hasHorizontalScroll={hasHorizontalScroll}
              />
            ))}
          {customButtonsLeft?.map((btn, idx) => {
            if (React.isValidElement(btn)) {
              // Если это кнопка с tooltip и есть горизонтальный скролл, применяем tooltipOptions
              if (
                hasHorizontalScroll &&
                btn.props.tooltip &&
                !btn.props.tooltipOptions
              ) {
                return React.cloneElement(btn as React.ReactElement<any>, {
                  key: btn.key ?? idx,
                  tooltipOptions: { position: 'left' }
                });
              }
              // Если tooltipOptions уже есть, обновляем только position
              if (
                hasHorizontalScroll &&
                btn.props.tooltip &&
                btn.props.tooltipOptions
              ) {
                return React.cloneElement(btn as React.ReactElement<any>, {
                  key: btn.key ?? idx,
                  tooltipOptions: {
                    ...btn.props.tooltipOptions,
                    position: 'left'
                  }
                });
              }
              return React.cloneElement(btn, { key: btn.key ?? idx });
            }
            return <React.Fragment key={idx}>{btn}</React.Fragment>;
          })}
        </div>
        <div
          className="flex align-items-center gap-2 table-buttons"
          style={
            isMobile
              ? { justifyContent: 'space-between', marginTop: 7 }
              : undefined
          }
        >
          <div className="flex align-items-center gap-2">
            {customButtonsRight}
          </div>
          <div className="flex align-items-center gap-2">
            {customAddButton
              ? customAddButton
              : onAdd && (
                  <Button
                    className={tableName + '-add-btn__roles'}
                    type="button"
                    icon="pi pi-plus"
                    severity="success"
                    rounded
                    onClick={onAdd}
                  />
                )}
            {onEdit && (
              <Button
                className={tableName + '-edit-btn__roles'}
                type="button"
                icon="pi pi-pencil"
                rounded
                onClick={onEdit}
              />
            )}
            {onDelete && (
              <Button
                className={tableName + '-delete-btn__roles'}
                type="button"
                icon="pi pi-trash"
                severity="danger"
                rounded
                onClick={onDelete}
              />
            )}
          </div>
        </div>
      </div>
      {additionalLine}
    </div>
  );
};

export default React.memo(TablePageHeader);
